/**
 * Bogue 1192 (Éric 2026-06-25) — « Garde la fenêtre et le curseur là où ils
 * sont, au niveau du mot modifié, sans retourner tout en haut de la liste.
 * C'est une règle générale pour toutes les actions sur toutes les listes du
 * LMS. »
 *
 * Pourquoi la page remontait : chaque action (renommer, fusionner, mettre en
 * forme…) se termine par un rechargement de la liste. Pendant ce
 * rechargement, la liste est remplacée par un état de chargement bien plus
 * court : le document rétrécit, le navigateur n'a plus la hauteur nécessaire
 * pour conserver la position et la ramène à 0. Quand la liste revient, il est
 * trop tard — l'utilisateur est en haut, loin du mot qu'il vient de toucher.
 *
 * On mémorise donc la position AVANT l'action et on la réapplique APRÈS que
 * le DOM a retrouvé sa hauteur.
 *
 * Bogue 1194 (Éric 2026-06-25) — « Propose un Retour Au Mot Modifié, en
 * surlignant pendant une seconde le terme qui vient d'être modifié. » Le
 * marquage est posé par l'action (`markModified`) et joué ici, juste après la
 * restauration : un seul point de passage, donc valable pour toutes les
 * actions de la page.
 */

const RESTORE_DELAY_MS = 260;
const FLASH_CLASS = "knoria-flash";
const FLASH_MS = 1000;

const _currentY = (container) => {
  if (container) return container.scrollTop || 0;
  if (typeof window === "undefined") return 0;
  return window.scrollY || document.documentElement.scrollTop || 0;
};

/** Position de défilement courante (fenêtre par défaut). */
export const scrollTop = (container = null) => _currentY(container);

const _maxY = (container) => {
  if (container) return Math.max(0, container.scrollHeight - container.clientHeight);
  if (typeof document === "undefined") return 0;
  const doc = document.documentElement;
  return Math.max(0, (doc.scrollHeight || 0) - (window.innerHeight || 0));
};

const _apply = (y, container) => {
  // Borné à la hauteur réelle : sinon on demande une position qui n'existe
  // plus (liste raccourcie par une fusion) et le navigateur l'ignore.
  const target = Math.min(y, _maxY(container));
  if (container) {
    container.scrollTop = target;
  } else if (typeof window !== "undefined") {
    window.scrollTo({ top: target, behavior: "auto" });
  }
};

// ── Bogue 1194 — marque déposée par l'action, jouée après la restauration ──
let _pendingFlash = null;

/**
 * Déclare le terme qui vient d'être modifié : il sera surligné une seconde
 * dès que la liste sera revenue. `testId` est la valeur `data-testid` de la
 * ligne (ex. `tags-page-row-retraite`, `jargon-entry-<id>`).
 */
export function markModified(testId) {
  _pendingFlash = testId || null;
}

/**
 * Surligne une seconde la ligne demandée, et la ramène dans la fenêtre si
 * l'action l'a poussée hors de vue (cas d'une fusion qui raccourcit la liste).
 * Attend l'arrivée de la ligne jusqu'à 1,5 s : après une mutation, la liste
 * est re-rendue de façon asynchrone.
 */
export function flashModified(testId) {
  if (!testId || typeof document === "undefined") return;
  const deadline = Date.now() + 1500;
  const tick = () => {
    const el = document.querySelector(`[data-testid="${testId}"]`);
    if (!el) {
      if (Date.now() < deadline) setTimeout(tick, 100);
      return;
    }
    const box = el.getBoundingClientRect();
    const hidden = box.bottom < 0 || box.top > (window.innerHeight || 0);
    if (hidden) el.scrollIntoView({ block: "center", behavior: "auto" });
    el.classList.remove(FLASH_CLASS);
    // Reflow forcé : sans ça, deux actions de suite sur la même ligne ne
    // rejouent pas l'animation.
    void el.offsetWidth;
    el.classList.add(FLASH_CLASS);
    setTimeout(() => el.classList.remove(FLASH_CLASS), FLASH_MS);
  };
  tick();
}

/**
 * Réapplique une position mémorisée, en laissant au DOM le temps de
 * retrouver sa hauteur (deux images puis un dernier passage), puis joue le
 * surlignage du terme modifié (bogue 1194).
 */
export function restoreScroll(y, container = null) {
  if (typeof window === "undefined") return;
  const flash = () => {
    if (_pendingFlash) {
      const id = _pendingFlash;
      _pendingFlash = null;
      flashModified(id);
    }
  };
  const again = () => {
    _apply(y, container);
    setTimeout(() => { _apply(y, container); flash(); }, RESTORE_DELAY_MS);
  };
  if (typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(() => window.requestAnimationFrame(again));
  } else {
    again();
  }
}

/**
 * Enveloppe une action de liste : la position est restaurée même si l'action
 * échoue (un renommage refusé ne doit pas non plus téléporter l'utilisateur).
 */
export async function withScrollPreserved(action, container = null) {
  const y = _currentY(container);
  try {
    return await action();
  } finally {
    restoreScroll(y, container);
  }
}
